import React, { useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  Stack,
  Divider,
  Chip,
  Paper,
  useTheme,
  Avatar,
  Skeleton,
  LinearProgress,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { useDataGridLocaleText } from "../../i18n/useDataGridLocale";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import HeightOutlinedIcon from "@mui/icons-material/HeightOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import SubdirectoryArrowRightOutlinedIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { fetchData, getConversationsList, getChatbotConversationSeries } from "../../api/client";
import { useChatbotSlots, useChatbotActions } from "../../hooks/useChatbot";
import { usePageContext } from "../../context/PageContext";
import { useTranslation } from "react-i18next";
import {
  computeChatbotFlowStats,
  aggregateConversationStatus,
  aggregateChannels,
  mergeChatbotDailySeries,
} from "../../utils/chatbotFlowStats";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
} from "recharts";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatAvg(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const CONVERSATION_SERIES_DAYS = 30;

function buildSeriesFromTheme(theme) {
  const p = theme.palette;
  return [
    p.primary.main,
    p.secondary?.main ?? p.info.main,
    p.success.main,
    p.warning.main,
    p.info.main,
    p.error.main,
  ];
}

function cardShellSx(theme) {
  return {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    border: 1,
    borderColor: "divider",
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 1px 4px ${alpha("#000", 0.35)}`
        : `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
  };
}

/** 2 colunas a partir de `sm`: encaixa qualquer número par de KPIs sem buracos na última linha (ex.: 14 → 7×2). */
function ChatbotKpiCard({ loading, icon, title, value, color, sub }) {
  const theme = useTheme();
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
      <Card elevation={1} sx={{ ...cardShellSx(theme), width: "100%" }}>
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: color,
                width: 44,
                height: 44,
                color: theme.palette.getContrastText(color),
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 22 } })}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

function ChatbotPanelCard({ title, subtitle, children, footer, compact, fillHeight }) {
  const theme = useTheme();
  const titleNode =
    typeof title === "string" ? (
      <Typography variant={compact ? "subtitle1" : "h6"} sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {title}
      </Typography>
    ) : (
      title
    );
  const padY = compact ? 1.5 : 2;
  const gapAfterDivider = compact ? 1.25 : 2;
  const cardStretch =
    compact && fillHeight
      ? {
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
        }
      : compact
        ? { height: "auto" }
        : { alignSelf: "stretch", height: "auto", flex: 1 };
  return (
    <Card
      elevation={1}
      sx={{
        ...cardShellSx(theme),
        overflow: "hidden",
        width: "100%",
        ...cardStretch,
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          pt: padY,
          pb: padY,
          px: 2,
          ...(compact && fillHeight ? { flex: 1, minHeight: 0 } : compact ? { flex: "none" } : { flex: 1 }),
        }}
      >
        <Box sx={{ mb: 0.25 }}>{titleNode}</Box>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}>
            {subtitle}
          </Typography>
        ) : null}
        <Divider sx={{ mb: gapAfterDivider }} />
        <Box
          sx={{
            flex: compact && fillHeight ? 1 : compact ? "none" : 1,
            flexGrow: compact && !fillHeight ? 0 : compact ? 1 : 1,
            minWidth: 0,
            ...(compact && fillHeight ? { display: "flex", flexDirection: "column", justifyContent: "flex-start" } : null),
          }}
        >
          {children}
        </Box>
        {footer ? <Box sx={{ mt: compact ? 1.5 : 2, flexShrink: 0 }}>{footer}</Box> : null}
      </CardContent>
    </Card>
  );
}

function SectionHeading({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, mt: 1 }}>
      {icon}
      <Typography component="h2" variant="h6" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Stack>
  );
}

/** Bloco compacto alinhado ao dashboard (avatar colorido + rótulo + valor). */
function AnalyticsStatTile({ icon, label, value, loading, accentColor, footer, dense }) {
  const theme = useTheme();
  const ac = accentColor ?? theme.palette.primary.main;
  if (loading) {
    return <Skeleton variant="rounded" height={dense ? 76 : 88} sx={{ borderRadius: 1.5, width: "100%" }} />;
  }
  return (
    <Box
      sx={{
        p: dense ? 1.25 : 1.5,
        borderRadius: 1.5,
        border: 1,
        borderColor: "divider",
        bgcolor: alpha(ac, theme.palette.mode === "dark" ? 0.14 : 0.07),
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 1px 3px ${alpha("#000", 0.22)}`
            : `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: dense ? 36 : 40,
            height: dense ? 36 : 40,
            flexShrink: 0,
            bgcolor: alpha(ac, 0.22),
            color: ac,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: dense ? 19 : 21 } })}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35, mb: 0.25 }}>
            {label}
          </Typography>
          <Typography variant={dense ? "h6" : "h5"} sx={{ fontWeight: 700, lineHeight: 1.15, color: "text.primary" }}>
            {value}
          </Typography>
          {footer}
        </Box>
      </Stack>
    </Box>
  );
}

function ActionTypeTiles({ flowStats, loading, theme, t, narrow }) {
  const c = flowStats?.tipoAcaoCounts || { none: 0, goto: 0, automation: 0, other: 0 };
  const total = c.none + c.goto + c.automation + c.other;
  const tileItemSm = narrow ? 12 : 6;
  const tiles = [
    {
      key: "none",
      labelKey: "chatbotsAnalyticsPage.actionNone",
      value: c.none,
      icon: <BlockOutlinedIcon />,
      color: theme.palette.text.secondary,
    },
    {
      key: "goto",
      labelKey: "chatbotsAnalyticsPage.actionGoto",
      value: c.goto,
      icon: <SubdirectoryArrowRightOutlinedIcon />,
      color: theme.palette.info.main,
    },
    {
      key: "automation",
      labelKey: "chatbotsAnalyticsPage.actionAutomation",
      value: c.automation,
      icon: <BoltOutlinedIcon />,
      color: theme.palette.warning.main,
    },
    {
      key: "other",
      labelKey: "chatbotsAnalyticsPage.actionOther",
      value: c.other,
      icon: <MoreHorizOutlinedIcon />,
      color: theme.palette.secondary?.main ?? theme.palette.info.main,
    },
  ];
  return (
    <Grid container spacing={narrow ? 1 : 1.5}>
      {tiles.map((tile) => (
        <Grid item xs={12} sm={tileItemSm} key={tile.key}>
          <AnalyticsStatTile
            icon={tile.icon}
            label={t(tile.labelKey)}
            value={tile.value}
            loading={loading}
            accentColor={tile.color}
            dense={narrow}
            footer={
              !loading && total > 0 ? (
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (tile.value / total) * 100)}
                  sx={{
                    mt: narrow ? 0.75 : 1,
                    height: 3,
                    borderRadius: 1,
                    bgcolor: alpha(tile.color, 0.14),
                    "& .MuiLinearProgress-bar": { borderRadius: 1, bgcolor: tile.color },
                  }}
                />
              ) : null
            }
          />
        </Grid>
      ))}
    </Grid>
  );
}

function FlowSummaryTiles({ flowStats, loading, theme, t, narrow }) {
  const steps = flowStats?.totalSteps ?? 0;
  const qr = flowStats?.withQuickReplies ?? 0;
  const depth = flowStats?.maxDepth ?? 0;
  const qrAccent = theme.palette.secondary?.main ?? theme.palette.info.main;
  const itemSm = narrow ? 12 : 4;
  return (
    <Grid container spacing={narrow ? 1 : 1.5}>
      <Grid item xs={12} sm={itemSm}>
        <AnalyticsStatTile
          icon={<AccountTreeOutlinedIcon />}
          label={t("chatbotsAnalyticsPage.cardSteps")}
          value={steps}
          loading={loading}
          accentColor={theme.palette.primary.main}
          dense={narrow}
        />
      </Grid>
      <Grid item xs={12} sm={itemSm}>
        <AnalyticsStatTile
          icon={<TouchAppOutlinedIcon />}
          label={t("chatbotsAnalyticsPage.cardQuickReplies")}
          value={qr}
          loading={loading}
          accentColor={qrAccent}
          dense={narrow}
        />
      </Grid>
      <Grid item xs={12} sm={itemSm}>
        <AnalyticsStatTile
          icon={<HeightOutlinedIcon />}
          label={t("chatbotsAnalyticsPage.cardMaxDepth")}
          value={depth}
          loading={loading}
          accentColor={theme.palette.success.main}
          dense={narrow}
        />
      </Grid>
    </Grid>
  );
}

export default function ChatbotAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dataGridLocaleText = useDataGridLocaleText();
  const { setCurrentPage } = usePageContext();
  const userId = id ? parseInt(id, 10) : null;

  const dashboardDataGridSx = useMemo(
    () => ({
      border: 0,
      width: "100%",
      "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
      "& .MuiDataGrid-columnHeaders": {
        borderBottom: 2,
        borderColor: "divider",
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07),
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
        fontSize: "0.8125rem",
      },
      "& .MuiDataGrid-row:hover": {
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.06 : 0.03),
        cursor: "default",
      },
      "& .MuiDataGrid-footerContainer": {
        borderTop: 1,
        borderColor: "divider",
      },
    }),
    [theme]
  );

  const { data: userRes, isLoading: loadingUser, error: userError } = useQuery(
    ["chatbot-user", id],
    async () => {
      const res = await fetchData(`/users/${id}`, "GET");
      return res?.data ?? res ?? null;
    },
    { enabled: !!id && !Number.isNaN(userId) }
  );

  const { data: slots = [], isLoading: loadingSlots } = useChatbotSlots(userId);
  const { data: actions = [], isLoading: loadingActions } = useChatbotActions(userId);

  const {
    data: convData,
    isLoading: loadingConv,
    error: convError,
  } = useQuery(
    ["chatbot-conversations", userId],
    () =>
      getConversationsList({
        limit: 50,
        tipo_scope: "todos",
        default_date_window_days: 0,
        ids_usuarios: userId,
      }),
    { enabled: !!userId && !Number.isNaN(userId), retry: false }
  );

  const conversations = convData?.list ?? [];

  const {
    data: seriesPayload,
    isLoading: loadingSeries,
    error: seriesError,
  } = useQuery(
    ["chatbot-conv-series", userId, CONVERSATION_SERIES_DAYS],
    () => getChatbotConversationSeries(userId, { days: CONVERSATION_SERIES_DAYS, tipo_scope: "todos" }),
    { enabled: !!userId && !Number.isNaN(userId), retry: false }
  );

  const timeSeriesChartData = useMemo(
    () =>
      mergeChatbotDailySeries(
        seriesPayload?.activity,
        seriesPayload?.created,
        seriesPayload?.days || CONVERSATION_SERIES_DAYS
      ),
    [seriesPayload]
  );

  const flowStats = useMemo(() => computeChatbotFlowStats(slots), [slots]);

  const statusChartData = useMemo(() => {
    const rows = aggregateConversationStatus(conversations);
    return rows
      .filter((r) => r.count > 0)
      .map((r) => ({
        name:
          r.code === "unknown"
            ? t("chatbotsAnalyticsPage.statusUnknown")
            : t(`chatbotsAnalyticsPage.status${r.code}`),
        value: r.count,
      }));
  }, [conversations, t]);

  const channelChartData = useMemo(() => aggregateChannels(conversations), [conversations]);

  const labelForStatus = useMemo(
    () => (st) => {
      const n = Number(st);
      if (!Number.isFinite(n)) return t("chatbotsAnalyticsPage.statusUnknown");
      return t(`chatbotsAnalyticsPage.status${n}`);
    },
    [t]
  );

  const SERIES = useMemo(() => buildSeriesFromTheme(theme), [theme]);

  const flowKpis = useMemo(() => {
    const lf = loadingSlots;
    const la = loadingActions;
    const fs = flowStats;
    return [
      { loading: lf, icon: <AccountTreeOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardSteps"), value: fs.totalSteps },
      { loading: lf, icon: <HeightOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardMaxDepth"), value: fs.maxDepth },
      { loading: lf, icon: <TouchAppOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardQuickReplies"), value: fs.withQuickReplies },
      { loading: lf, icon: <RuleOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardConditions"), value: fs.withConditions },
      { loading: lf, icon: <BoltOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardActions"), value: fs.withActions },
      { loading: lf, icon: <ErrorOutlineOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardExceptions"), value: fs.exceptionSteps },
      { loading: lf, icon: <PublicOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardGlobal"), value: fs.globalSteps },
      { loading: lf, icon: <BlockOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardIgnoreGlobal"), value: fs.ignoreGlobalSteps },
      { loading: lf, icon: <AttachFileOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardFiles"), value: fs.withFile },
      { loading: lf, icon: <ScheduleOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardSchedule"), value: fs.withSchedule },
      {
        loading: lf,
        icon: <ChatBubbleOutlineOutlinedIcon />,
        title: t("chatbotsAnalyticsPage.cardResponses"),
        value: fs.withResponse,
      },
      { loading: lf, icon: <CallSplitOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardAvgBranch"), value: formatAvg(fs.avgDirectChildren) },
      { loading: lf, icon: <CallSplitOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardMaxBranch"), value: fs.maxDirectChildren },
      { loading: la, icon: <HubOutlinedIcon />, title: t("chatbotsAnalyticsPage.cardCatalogActions"), value: actions.length },
    ];
  }, [t, loadingSlots, loadingActions, flowStats, actions.length]);

  const recent = useMemo(() => conversations.slice(0, 12), [conversations]);

  const branchRows = useMemo(
    () =>
      (loadingSlots ? [] : flowStats.topByChildren).map((row) => ({
        id: row.id,
        identificador: row.identificador,
        children: row.children,
        depth: row.depth,
      })),
    [loadingSlots, flowStats.topByChildren]
  );

  const branchColumns = useMemo(
    () => [
      {
        field: "identificador",
        headerName: t("chatbotsAnalyticsPage.colStep"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "children",
        headerName: t("chatbotsAnalyticsPage.colChildren"),
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
      {
        field: "depth",
        headerName: t("chatbotsAnalyticsPage.colDepth"),
        width: 110,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
    ],
    [t]
  );

  const labelRows = useMemo(
    () =>
      (loadingSlots ? [] : flowStats.topIdentifiers).map((row, i) => ({
        id: `${row.name}-${i}`,
        identifier: row.name,
        count: row.count,
      })),
    [loadingSlots, flowStats.topIdentifiers]
  );

  const labelColumns = useMemo(
    () => [
      {
        field: "identifier",
        headerName: t("chatbotsAnalyticsPage.colIdentifier"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "count",
        headerName: t("chatbotsAnalyticsPage.colOccurrences"),
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
    ],
    [t]
  );

  const conversationRows = useMemo(
    () =>
      (loadingConv ? [] : recent).map((c) => {
        const cid = c.id ?? c.leadId;
        return {
          id: cid,
          name: c.name || `#${cid}`,
          channel: c.channel || "—",
          status: c.status,
          updatedAt: c.timestamp ?? c.updatedAt,
        };
      }),
    [loadingConv, recent]
  );

  const conversationColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("chatbotsAnalyticsPage.colConversation"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "channel",
        headerName: t("chatbotsAnalyticsPage.colChannel"),
        width: 120,
      },
      {
        field: "status",
        headerName: t("chatbotsAnalyticsPage.colStatus"),
        width: 150,
        renderCell: (p) => (
          <Chip size="small" label={labelForStatus(p.row.status)} variant="outlined" color="primary" />
        ),
      },
      {
        field: "updatedAt",
        headerName: t("chatbotsAnalyticsPage.colUpdated"),
        width: 190,
        renderCell: (p) => formatDate(p.row.updatedAt),
      },
      {
        field: "actions",
        headerName: t("shared.actions"),
        width: 120,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => (
          <Button size="small" onClick={() => navigate(`/chat/${encodeURIComponent(String(p.row.id))}`)}>
            {t("chatbotsAnalyticsPage.openChat")}
          </Button>
        ),
      },
    ],
    [t, navigate, labelForStatus]
  );

  useEffect(() => {
    const name = userRes?.nome ?? t("chatbotConfigPage.title");
    setCurrentPage({ title: `${t("chatbotsAnalyticsPage.title")} — ${name}`, showBackButton: false });
  }, [setCurrentPage, userRes?.nome, t]);

  if (!id || Number.isNaN(userId)) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="text.secondary">{t("chatbotFormPage.invalidId")}</Typography>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/chatbots")}>
          {t("chatbotFormPage.backToList")}
        </Button>
      </Box>
    );
  }

  if (userError && !userRes) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">{t("chatbotsAnalyticsPage.loadUserError")}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/chatbots")}>
          {t("chatbotFormPage.backToList")}
        </Button>
      </Box>
    );
  }

  const botName = userRes?.nome ?? id;
  const showTopProgress = loadingUser && !userRes;

  return (
    <Box
      className="tour-chatbot-analytics"
      sx={{
        p: 1,
        pb: 4,
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      {showTopProgress ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h4" sx={{ textAlign: isMobile ? "left" : "left", mb: 0.5 }}>
            {t("chatbotsAnalyticsPage.heading")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("chatbotsAnalyticsPage.intro", { name: botName })}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent={isMobile ? "stretch" : "flex-end"}
          sx={{ flexShrink: 0 }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(`/chatbots/${id}`)}
            fullWidth={isMobile}
          >
            {t("chatbotsAnalyticsPage.backToEditor")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ListAltOutlinedIcon />}
            onClick={() => navigate("/chatbots")}
            fullWidth={isMobile}
          >
            {t("chatbotsAnalyticsPage.backToList")}
          </Button>
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" component="span" display="block">
          {t("chatbotsAnalyticsPage.structuralHint")}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="span" display="block" sx={{ mt: 0.75 }}>
          {t("chatbotsAnalyticsPage.seriesExplanation")}
        </Typography>
      </Alert>

      <SectionHeading icon={<AccountTreeOutlinedIcon color="primary" fontSize="small" />}>
        {t("chatbotsAnalyticsPage.sectionFlow")}
      </SectionHeading>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {flowKpis.map((k, i) => (
          <ChatbotKpiCard
            key={k.title}
            loading={k.loading}
            icon={k.icon}
            title={k.title}
            value={k.value}
            color={SERIES[i % SERIES.length]}
          />
        ))}
        <ChatbotKpiCard
          loading={loadingSeries}
          icon={flowKpis[0]?.icon}
          title="Economia vs humano"
          value={
            seriesPayload?.labor_cost_equivalent?.economia_estimada != null
              ? `R$ ${Number(seriesPayload.labor_cost_equivalent.economia_estimada).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
              : "—"
          }
          color={SERIES[flowKpis.length % SERIES.length]}
          sub={
            seriesPayload?.labor_cost_equivalent?.note ||
            "Equivalente ao custo se humanos tivessem feito esses atendimentos"
          }
        />
      </Grid>

      {/* Tabelas em cima (50/50); ações + resumo em baixo (50/50) — evita coluna estreita com muito espaço vazio */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChatbotPanelCard title={t("chatbotsAnalyticsPage.topBranchTitle")}>
            <Box sx={{ width: "100%", minHeight: 120 }}>
              <DataGrid
                rows={branchRows}
                columns={branchColumns}
                loading={loadingSlots}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                localeText={dataGridLocaleText}
                sx={dashboardDataGridSx}
              />
            </Box>
          </ChatbotPanelCard>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChatbotPanelCard title={t("chatbotsAnalyticsPage.topLabelsTitle")}>
            <Box sx={{ width: "100%", minHeight: 120 }}>
              <DataGrid
                rows={labelRows}
                columns={labelColumns}
                loading={loadingSlots}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                localeText={dataGridLocaleText}
                sx={dashboardDataGridSx}
              />
            </Box>
          </ChatbotPanelCard>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChatbotPanelCard compact title={t("chatbotsAnalyticsPage.sectionActions")}>
            <ActionTypeTiles flowStats={flowStats} loading={loadingSlots} theme={theme} t={t} />
          </ChatbotPanelCard>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChatbotPanelCard compact title={t("chatbotsAnalyticsPage.rawHeading")}>
            <FlowSummaryTiles flowStats={flowStats} loading={loadingUser || loadingSlots} theme={theme} t={t} />
          </ChatbotPanelCard>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <SectionHeading icon={<ForumOutlinedIcon color="primary" fontSize="small" />}>
        {t("chatbotsAnalyticsPage.sectionConv")}
      </SectionHeading>

      {seriesError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("chatbotsAnalyticsPage.seriesLoadError")}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <Grid item xs={12} sx={{ display: "flex" }}>
          <ChatbotPanelCard
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <ShowChartOutlinedIcon fontSize="small" color="primary" />
                <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
                  {t("chatbotsAnalyticsPage.chartTimeSeriesTitle")}
                </Typography>
              </Stack>
            }
            subtitle={t("chatbotsAnalyticsPage.chartTimeSeriesSubtitle")}
          >
            <Box sx={{ width: "100%", height: { xs: 260, sm: 300 } }}>
              <ResponsiveContainer>
                <LineChart
                  data={loadingSeries ? [] : timeSeriesChartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 10 }}
                    angle={-30}
                    textAnchor="end"
                    height={52}
                    interval="preserveStartEnd"
                    tickFormatter={(v) => {
                      if (!v || typeof v !== "string") return "";
                      const d = new Date(`${v}T12:00:00`);
                      return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                    }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RTooltip
                    labelFormatter={(v) => {
                      if (!v || typeof v !== "string") return "";
                      const d = new Date(`${v}T12:00:00`);
                      return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="activityCount"
                    name={t("chatbotsAnalyticsPage.chartSeriesActivity")}
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="createdCount"
                    name={t("chatbotsAnalyticsPage.chartSeriesCreated")}
                    stroke={theme.palette.secondary?.main ?? theme.palette.info.main}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </ChatbotPanelCard>
        </Grid>
      </Grid>

      {convError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("chatbotsAnalyticsPage.loadConvError")}
        </Alert>
      )}

      {convError ? (
        <Alert severity="info" sx={{ mb: 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            {t("chatbotsAnalyticsPage.liveSampleHint")}
          </Typography>
        </Alert>
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: "stretch" }}>
          <Alert severity="info" sx={{ flex: 1, py: 1.25, alignItems: "flex-start" }}>
            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
              {t("chatbotsAnalyticsPage.liveSampleHint")}
            </Typography>
          </Alert>
          <Paper
            variant="outlined"
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.04),
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.primary.main, 0.18), color: "primary.main" }}>
              <ForumOutlinedIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.35 }}>
              {t("chatbotsAnalyticsPage.sampleSize", { count: conversations.length })}
            </Typography>
          </Paper>
        </Stack>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChatbotPanelCard
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <BarChartRoundedIcon fontSize="small" color="primary" />
                <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
                  {t("chatbotsAnalyticsPage.chartStatusTitle")}
                </Typography>
              </Stack>
            }
          >
            {!loadingConv && statusChartData.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center", bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography color="text.secondary">{t("chatbotsAnalyticsPage.emptyConversations")}</Typography>
              </Box>
            ) : (
              <Box sx={{ width: "100%", height: { xs: 220, sm: 260 } }}>
                <ResponsiveContainer>
                  <BarChart data={loadingConv ? [] : statusChartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="name" interval={0} angle={-25} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RTooltip />
                    <Bar
                      dataKey="value"
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                      name={t("chatbotsAnalyticsPage.countAxis")}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </ChatbotPanelCard>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChatbotPanelCard
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <BarChartRoundedIcon fontSize="small" color="primary" />
                <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
                  {t("chatbotsAnalyticsPage.chartChannelTitle")}
                </Typography>
              </Stack>
            }
          >
            {!loadingConv && channelChartData.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center", bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography color="text.secondary">{t("chatbotsAnalyticsPage.emptyConversations")}</Typography>
              </Box>
            ) : (
              <Box sx={{ width: "100%", height: { xs: 220, sm: 260 } }}>
                <ResponsiveContainer>
                  <BarChart
                    data={loadingConv ? [] : channelChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <RTooltip />
                    <Bar
                      dataKey="value"
                      fill={theme.palette.secondary?.main ?? theme.palette.info.main}
                      radius={[0, 4, 4, 0]}
                      name={t("chatbotsAnalyticsPage.countAxis")}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </ChatbotPanelCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sx={{ display: "flex" }}>
          <ChatbotPanelCard title={t("chatbotsAnalyticsPage.recentTitle")}>
            <Box sx={{ width: "100%", minHeight: 200 }}>
              <DataGrid
                rows={conversationRows}
                columns={conversationColumns}
                loading={loadingConv}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                localeText={dataGridLocaleText}
                sx={dashboardDataGridSx}
              />
            </Box>
          </ChatbotPanelCard>
        </Grid>
      </Grid>
    </Box>
  );
}
